import React, { ReactElement, use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import moment, { Moment } from 'moment';
import { useSessionStorage } from 'usehooks-ts';
import { AlarmPriority } from '../../../shared/components/alarm-card/enums/alarm-priority';
import { AlarmStatus } from '../../../shared/components/alarm-card/enums/alarm-status';
import { AlarmType } from '../../../shared/components/alarm-card/enums/alarm-type';
import { HistoryItemType } from '../../../shared/components/timeline/enums/history-item-type';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import GoogleMapsIcon from 'shared/components/icons/google-maps-icon';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PageHeader from 'shared/components/page-header/page-header';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import useTableFiltering from 'shared/components/table/hooks/table-filtering';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import ObjectSort from 'shared/models/object-sort';
import { formatFromMilliseconds } from 'shared/utils/format-from-milliseconds';

import ReadHistoryCommand from 'features/history/domain/models/read-history-command';
import { useLazyGetHistory } from 'features/history/domain/queries/history-queries';
import styles from 'features/history/views/history.component.module.scss';
import { ExternalLocationCoordinates } from '../domain/models/external-location-coordinates';
import { HistoryItem } from '../domain/models/history-item';
import { useHistoryContextProvider } from '../providers/history-provider';
import Category from './category.component';
import HistoryDetails from './details/history-details.component';
import HistoryFilters, { HistoryFilterValues } from './history-filters';

const History = (): React.JSX.Element => {
  const { t } = useTranslation('history');
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const sessionStorageFiltersKey = FilterSessionStorageKeys.HISTORY_FILTERS;
  const sessionStorageSortingKey = FilterSessionStorageKeys.HISTORY_SORTING;
  const { currentSelectedItem, setCurrentSelectedItem, setRefreshHistory } = useHistoryContextProvider();
  const { hasPermission } = usePermissionAuthorization();

  const [originalHistory, setOriginalHistory] = useState<HistoryItem[]>([]);

  const { currentPage, currentRowsPerPage, handleOnPageChanged, handleOnRowsPerPageChanged } = useTableFiltering();

  const [filtersFromSessionStorage] = useSessionStorage<HistoryFilterValues>(sessionStorageFiltersKey, {});
  const [filters, setFilters] = useState<HistoryFilterValues>(filtersFromSessionStorage);

  const [sortFromSessionStorage, setSortFromSessionStorage] = useSessionStorage<ObjectSort<HistoryItem>>(sessionStorageSortingKey, {
    property: 'createdOn',
    isAscending: false,
  });

  const [command, setCommand] = useState<ReadHistoryCommand>({} as never);
  const {
    refetch: triggerReadHistory,
    data: readHistoryData,
    isSuccess: readHistoryIsSuccess,
    error: readHistoryError,
    isFetching: readHistoryIsFetching,
  } = useLazyGetHistory(command);

  const isGenericOrEmergency = (item: HistoryItem): boolean => item.type === AlarmType.GenericCall || item.type === AlarmType.Emergency;

  const showInMapClicked = (coordinates: ExternalLocationCoordinates): void => {
    window.open(`https://maps.google.com?q=${coordinates.latitude},${coordinates.longitude}`);
  };

  const columns: ColumnDefinition<HistoryItem, NestedKeyof<HistoryItem>>[] = [
    {
      key: 'id',
      label: t('historyTable.column.category'),
      renderCustomContentProvider: (item) => (
        <Category alarmPriority={item.priority ?? AlarmPriority.Unknown} historyType={item.historyType} />
      ),
      disableSort: true,
    },
    {
      key: 'type',
      label: t('historyTable.column.type'),
      renderCustomContentProvider: (item) => (
        <div className={styles.alarmType}>
          <Typography variant="subtitle1">
            {isGenericOrEmergency(item) ? item.message : t(`type.${item.historyType}.${item.type}`)}
          </Typography>
          {item.message && !isGenericOrEmergency(item) && <Typography variant="caption">{item.message}</Typography>}
          {item.nearbyText && <Typography variant="caption">{`${t('type.Alarm.nearby')} ${item.nearbyText}`}</Typography>}
          {item.externalLocation && (
            <div>
              {!item.nearbyText && <Typography variant="caption">{t('type.Alarm.nearby')}</Typography>}
              <Link
                onClick={(event) => {
                  if (item.externalLocation) {
                    showInMapClicked(item.externalLocation);
                  }
                  event.stopPropagation();
                }}
              >
                <GoogleMapsIcon
                  sx={{
                    fontSize: '16.5px',
                  }}
                />
                <Typography variant="caption">{t('type.Alarm.lookAtMap')}</Typography>
              </Link>
            </div>
          )}
        </div>
      ),
      disableSort: true,
    },
    {
      key: 'organisationUnitsWithAncestors.length',
      label: t('historyTable.column.breadCrumbs'),
      renderCustomContentProvider: (item) => (
        <div className={styles.breadCrumbContainer}>
          {item?.organisationUnitsWithAncestors.map((organisationUnits, i) => (
            <BreadCrumb
              key={`breadcrumb-${item.id}-${i}`}
              showEllipsis={organisationUnits.length > 2}
              allElementNames={organisationUnits.map((unit) => unit.name)}
              data-testid={`history-breadcrumb-child-${item.id}`}
            >
              {organisationUnits?.map((unit, index) => {
                return (
                  <Typography
                    data-testid={`history-breadcrumb-child-${index}`}
                    key={`History_Breadcrumb_Child_${unit.name}`}
                    variant="caption"
                    className={styles.breadCrumbItem}
                  >
                    {unit.name}
                  </Typography>
                );
              })}
            </BreadCrumb>
          ))}
        </div>
      ),
      disableSort: true,
    },
    {
      key: 'createdOn',
      label: t('historyTable.column.createdOn'),
      renderCustomContentProvider: (item) => <>{moment(item.createdOn).format('DD-MM-YYYY · HH:mm:ss').toString()}</>,
      disableSort: false,
    },
    {
      key: 'statusLastChangedAt',
      label: t('historyTable.column.statusLastChangedAt'),
      renderCustomContentProvider: (item): ReactElement => {
        if (item.status === AlarmStatus.Handled && item.statusLastChangedAt) {
          return <>{moment(item.statusLastChangedAt).format('DD-MM-YYYY · HH:mm:ss').toString()}</>;
        } else if (item.status !== AlarmStatus.Handled && item.historyType === HistoryItemType.Alarm) {
          return (
            <div
              className={`${styles.status} ${
                item.status === AlarmStatus.Reported ? styles.backgroundColorReported : styles.backgroundColorAccepted
              }`}
            >
              <Typography className={styles.statusText} variant="overline">
                {t(`status.${item.status}`)}
              </Typography>
            </div>
          );
        }

        return <></>;
      },
      disableSort: false,
    },
    {
      key: 'duration',
      label: t('historyTable.column.duration'),
      renderCustomContentProvider: (item) => <>{getDurationWithDaysInHours(item)}</>,
      disableSort: false,
    },
    {
      key: 'isDiverted',
      label: '',
      renderCustomContentProvider: (alarm) =>
        alarm.isDiverted ? (
          <Typography className={styles.isDiverted} variant="overline">
            {t('isDiverted')}
          </Typography>
        ) : (
          <></>
        ),
      disableSort: true,
    },
  ];

  const getDurationWithDaysInHours = (item: HistoryItem): string => {
    if (item.historyType === HistoryItemType.Status || (item.type === AlarmType.EmergencyAlarm && item.status === AlarmStatus.Cancelled)) {
      return '';
    }

    const duration = moment.duration(item.duration);

    return formatFromMilliseconds(duration.asMilliseconds());
  };

  const getDate = (date: Moment | null | undefined): Date | undefined => {
    if (date) {
      return new Date(date.toString());
    }
    return undefined;
  };

  const triggerReadHistoryPeriod = (): void => {
    const from = getDate(filters?.dateFilters?.dateFrom);
    const to = getDate(filters?.dateFilters?.dateTo);

    const newCommand: ReadHistoryCommand = {
      from: from,
      to: to,
      category: filters?.categoryFilter,
      types: filters?.typeFilters ?? [],
      organisationUnitIds: filters?.organisationUnitFilters?.map((x) => x.id) ?? [],
      isDiverted: filters?.diversionFilter !== undefined ? filters?.diversionFilter === 'true' : undefined,
      status: filters?.statusFilter,

      currentPage: currentPage,
      currentRowsPerPage: currentRowsPerPage,

      sort: sortFromSessionStorage.property,
      isAscending: sortFromSessionStorage.isAscending,
    };

    if (command !== newCommand) {
      setCommand(newCommand);
    }
  };

  useEffect(() => {
    if (command && Object.keys(command).length > 0) {
      triggerReadHistory();
    }
  }, [command]);

  useEffect(() => {
    setRefreshHistory(triggerReadHistory);
  }, [setRefreshHistory, triggerReadHistory]);

  useEffect(() => {
    if (readHistoryData && readHistoryData.historyRows.length > 0 && originalHistory.length === 0) {
      setOriginalHistory(readHistoryData.historyRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readHistoryData]);

  useEffect(triggerReadHistoryPeriod, [filters, sortFromSessionStorage, currentPage, currentRowsPerPage]);

  useEffect(
    () => {
      handleOnPageChanged(0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters],
  );

  useEffect(() => {
    if (readHistoryError) {
      setErrorMessage(readHistoryError);
    }
  }, [readHistoryError]);

  return (
    <div className={styles.historyContainer}>
      <PageHeader
        title={t('pageHeaderTitle')}
        filterComponent={
          readHistoryIsSuccess ? (
            <div className={styles.historyFilterComponentContainer}>
              <HistoryFilters
                historyItems={originalHistory}
                onFilterChanged={setFilters}
                filterOptions={readHistoryData?.filterOptions ?? []}
                allMessageTypes={readHistoryData?.allMessageTypes ?? []}
              />
              <IconButton className={styles.refreshButton} onClick={triggerReadHistoryPeriod}>
                <RefreshRounded />
              </IconButton>
            </div>
          ) : (
            <></>
          )
        }
      />
      {readHistoryIsFetching && <LoadingIndicator />}
      {readHistoryIsSuccess && readHistoryData && (
        <Table
          data={readHistoryData.historyRows.map<RowItem<HistoryItem>>((e: HistoryItem) => ({
            isHighlighted:
              e.status !== AlarmStatus.Handled && e.status !== AlarmStatus.Cancelled && e.historyType === HistoryItemType.Alarm,
            isClickable: e.historyType === HistoryItemType.Alarm,
            data: e,
          }))}
          columns={columns}
          pagination={{
            count: readHistoryData.allItemsCount,
            onPageChanged: handleOnPageChanged,
            rowsPerPage: currentRowsPerPage,
            page: currentPage,
            onRowsPerPageChanged: handleOnRowsPerPageChanged,
          }}
          rowIdentifier={(item: HistoryItem) => item.id}
          onSortChanged={(property: NestedKeyof<HistoryItem>, isAscending: boolean) => setSortFromSessionStorage({ property, isAscending })}
          initialOrderBy={sortFromSessionStorage.property}
          initialOrderDirection={sortFromSessionStorage.isAscending ? 'asc' : 'desc'}
          onItemClick={hasPermission(Permission.ReadHistoryDetails) ? (item: HistoryItem): void => setCurrentSelectedItem(item) : undefined}
        />
      )}
      {currentSelectedItem && hasPermission(Permission.ReadHistoryDetails) && <HistoryDetails />}
    </div>
  );
};

export default History;
